import React, { useState, useEffect } from "react";
import {
  ConstrainedDialog as Dialog,
  ConstrainedDialogContent as DialogContent,
  ConstrainedDialogDescription as DialogDescription,
  ConstrainedDialogFooter as DialogFooter,
  ConstrainedDialogHeader as DialogHeader,
  ConstrainedDialogTitle as DialogTitle,
} from "@/components/ui/constrained-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Interview } from "@/services/interviewService";
import { DollarSign, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface AddExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (expenseData: Partial<Interview>) => void;
  projectId?: string;
}

const AddExpenseModal: React.FC<AddExpenseModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  projectId,
}) => {
  const [description, setDescription] = useState("");
  const [company, setCompany] = useState("");
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [cost, setCost] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setDescription("");
      setCompany("");
      setDate(new Date());
      setCost("");
      setNotes("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!description.trim() || !cost) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Set date to midnight (12:00 AM) to avoid time selection
      const normalizedDate = date ? new Date(date) : new Date();
      normalizedDate.setHours(0, 0, 0, 0);

      // Create expense data formatted as an Interview with status "secondary report"
      const expenseData: Partial<Interview> = {
        status: "secondary report",
        title: description.trim(), // Report description goes to title
        company: company.trim() || "Unknown Provider", // Company that made the report
        angle: notes.trim(), // Additional notes go to angle
        date: normalizedDate,
        initialInterviewCost: cost,
        // Set required fields to default values since this is an expense, not an interview
        expertFirstName: "N/A",
        expertLastName: "N/A",
        network: "Secondary Research",
        duration: "0",
        questionnaireCost: "0",
      };

      await onSubmit(expenseData);
      handleClose();
    } catch (error) {
      console.error("Error submitting expense:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Secondary Report Expense</DialogTitle>
          <DialogDescription>
            Add a secondary or market research report expense to track against
            your project budget.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Description Field */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium">
                Report Description <span className="text-red-500">*</span>
              </Label>
              <Input
                id="description"
                placeholder="e.g., Market sizing report for healthcare sector"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                className="w-full"
              />
            </div>

            {/* Company Field */}
            <div className="space-y-2">
              <Label htmlFor="company" className="text-sm font-medium">
                Source <span className="text-red-500">*</span>
              </Label>
              <Input
                id="company"
                placeholder="e.g., Gartner, Forrester, McKinsey"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                required
                className="w-full"
              />
            </div>

            {/* Date Field */}
            <div className="space-y-2">
              <Label htmlFor="date" className="text-sm font-medium">
                Purchase Date <span className="text-red-500">*</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Cost Field */}
            <div className="space-y-2">
              <Label htmlFor="cost" className="text-sm font-medium">
                Cost (USD) <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="cost"
                  type="number"
                  placeholder="0.00"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  required
                  min="0"
                  step="0.01"
                  className="pl-9"
                />
              </div>
            </div>

            {/* Notes Field (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-sm font-medium">
                Additional Notes (Optional)
              </Label>
              <Textarea
                id="notes"
                placeholder="Any additional context or details about this report..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !description.trim() || !company.trim() || !cost || isSubmitting
              }
            >
              {isSubmitting ? "Adding..." : "Add Expense"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddExpenseModal;
